/**
 * Service de gestion des permissions basé sur les permissions de l'utilisateur courant
 */
export default {
    /**
     * Vérifie si l'utilisateur a la permission spécifiée
     * @param {string|Array|Object} permission - Permission(s) à vérifier
     * @param {Array} userPermissions - Permissions de l'utilisateur (optionnel, sinon pris du store)
     * @returns {boolean}
     */
    check(permission, userPermissions = null) {
        if (!permission) return true;

        // Récupérer les permissions de l'utilisateur
        const permissions = userPermissions || this.getUserPermissions();
        const permissionsMap = this.convertToMap(permissions);

        // Déterminer le type de permission et vérifier
        if (typeof permission === 'string') {
            return this.checkSinglePermission(permission, permissionsMap);
        }

        if (Array.isArray(permission)) {
            return this.checkMultiplePermissions(permission, permissionsMap);
        }

        if (typeof permission === 'object') {
            return this.checkPermissionObject(permission, permissionsMap);
        }

        return false;
    },

    /**
     * Alias pour check()
     */
    has(permission, userPermissions = null) {
        return this.check(permission, userPermissions);
    },

    /**
     * Vérifie une seule permission
     * @param {string} permissionKey - Clé de permission
     * @param {Object} permissionsMap - Map des permissions utilisateur
     * @returns {boolean}
     */
    checkSinglePermission(permissionKey, permissionsMap) {
        return parseFloat(permissionsMap[permissionKey]) === 1;
    },

    /**
     * Vérifie plusieurs permissions (logique ET par défaut)
     * @param {Array} permissions - Tableau de permissions
     * @param {Object} permissionsMap - Map des permissions utilisateur
     * @param {string} logic - 'AND' ou 'OR'
     * @returns {boolean}
     */
    checkMultiplePermissions(permissions, permissionsMap, logic = 'AND') {
        if (!Array.isArray(permissions) || permissions.length === 0) {
            return false;
        }

        if (logic === 'AND') {
            return permissions.every(permission =>
                this.checkSinglePermission(permission, permissionsMap)
            );
        }

        if (logic === 'OR') {
            return permissions.some(permission =>
                this.checkSinglePermission(permission, permissionsMap)
            );
        }

        return false;
    },

    /**
     * Vérifie un objet de permissions avec logique ET/OU
     * @param {Object} permissionObj - Objet de permissions
     * @param {Object} permissionsMap - Map des permissions utilisateur
     * @returns {boolean}
     */
    checkPermissionObject(permissionObj, permissionsMap) {
        // Cas 1: {ET: ['perm1', 'perm2']}
        if (permissionObj.ET || permissionObj.ALL) {
            const permissions = permissionObj.ET || permissionObj.ALL;
            return this.checkMultiplePermissions(permissions, permissionsMap, 'AND');
        }

        // Cas 2: {OU: ['perm1', 'perm2']}
        if (permissionObj.OU || permissionObj.ANY) {
            const permissions = permissionObj.OU || permissionObj.ANY;
            return this.checkMultiplePermissions(permissions, permissionsMap, 'OR');
        }

        // Cas 3: {ET: [...], OU: [...]} - Les deux doivent être vrais
        if (permissionObj.ET && permissionObj.OU) {
            const hasAll = this.checkMultiplePermissions(permissionObj.ET, permissionsMap, 'AND');
            const hasAny = this.checkMultiplePermissions(permissionObj.OU, permissionsMap, 'OR');
            return hasAll && hasAny;
        }

        // Cas 4: Objet avec d'autres structures
        return false;
    },

    /**
     * Convertit un tableau de permissions en objet map
     * @param {Array} permissionsArray - Tableau de permissions
     * @returns {Object}
     */
    convertToMap(permissionsArray) {
        if (!Array.isArray(permissionsArray)) {
            return {};
        }

        const permissionsMap = {};
        permissionsArray.forEach(permission => {
            permissionsMap[permission] = 1;
        });

        return permissionsMap;
    },

    /**
     * Récupère les permissions de l'utilisateur
     * @returns {Array}
     */
    getUserPermissions() {
        // Si vous avez accès au store Vuex
        if (window.$store && window.$store.state && window.$store.state.currentuserpermissions) {
            return window.$store.state.currentuserpermissions;
        }

        // Sinon, essayez localStorage
        try {
            const userData = localStorage.getItem('user');
            if (userData) {
                const user = JSON.parse(userData);
                return user.permissions || [];
            }
        } catch (e) {
            console.error('Erreur de lecture des permissions:', e);
        }

        return [];
    },

    /**
     * Méthodes utilitaires pour une syntaxe plus claire
     */
    // Vérifie toutes les permissions (ET)
    hasAll(permissions, userPermissions = null) {
        if (!Array.isArray(permissions)) return false;
        const perms = userPermissions || this.getUserPermissions();
        const permissionsMap = this.convertToMap(perms);
        return this.checkMultiplePermissions(permissions, permissionsMap, 'AND');
    },

    // Vérifie au moins une permission (OU)
    hasAny(permissions, userPermissions = null) {
        if (!Array.isArray(permissions)) return false;
        const perms = userPermissions || this.getUserPermissions();
        const permissionsMap = this.convertToMap(perms);
        return this.checkMultiplePermissions(permissions, permissionsMap, 'OR');
    },

    // Vérifie la permission dans un contexte de menu spécifique
    hasMenuPermission(menu, action, userPermissions = null) {
        const permission = `${menu}.${action}`;
        return this.check(permission, userPermissions);
    },

    // Vérifie plusieurs permissions pour un menu
    hasAllMenuPermissions(menu, actions, userPermissions = null) {
        const permissions = actions.map(action => `${menu}.${action}`);
        return this.hasAll(permissions, userPermissions);
    },

    hasAnyMenuPermissions(menu, actions, userPermissions = null) {
        const permissions = actions.map(action => `${menu}.${action}`);
        return this.hasAny(permissions, userPermissions);
    }
};


/* UTILISATION



import permissionHelper from '@/utils/permissionHelper';

export default {
  computed: {
    showAddButton() {
      // Récupérer les permissions depuis le store
      const userPermissions = this.$store.state.currentuserpermissions;

      // Exemple 1: Vérifier une seule permission
      return permissionHelper.check('menu1.add', userPermissions);

      // Exemple 2: Vérifier plusieurs permissions (ET)
      return permissionHelper.check(['menu1.add', 'menu1.write'], userPermissions);

      // Exemple 3: Vérifier plusieurs permissions (OU)
      return permissionHelper.check({OU: ['menu1.add', 'menu1.write']}, userPermissions);

      // Exemple 4: Vérifier plusieurs permissions (ET et OU combinés)
      return permissionHelper.check({
        ET: ['menu1.view', 'menu1.read'],
        OU: ['menu1.add', 'menu1.write']
      }, userPermissions);
    }
  }
};

















 */


